import axios from 'axios';

// WordPress.com API configuration
// For WordPress.com sites, the API URL is different from self-hosted WordPress
// WordPress.com uses: https://public-api.wordpress.com/rest/v1.1/sites/SITE_URL/posts/
const WORDPRESS_COM_SITE = 'sharpcutai.wordpress.com'; // Your WordPress.com site
const WORDPRESS_COM_API_URL = `https://public-api.wordpress.com/rest/v1.1/sites/${WORDPRESS_COM_SITE}`;
const POSTS_PER_PAGE = 6;

// TypeScript interfaces for WordPress.com API data (slightly different from self-hosted)
export interface WordPressComPost {
  ID: number;
  date: string;
  modified: string;
  title: string;
  content: string;
  excerpt: string;
  slug: string;
  URL: string; // The full URL to the post on WordPress.com
  author: {
    ID: number;
    login: string;
    email: boolean;
    name: string;
    first_name: string;
    last_name: string;
    nice_name: string;
    URL: string;
    avatar_URL: string;
    profile_URL: string;
    site_ID: number;
  };
  featured_image?: string;
  post_thumbnail?: {
    ID: number;
    URL: string;
    guid: string;
    mime_type: string;
    width: number;
    height: number;
  };
  tags: {
    [key: string]: {
      ID: number;
      name: string;
      slug: string;
      description: string;
      post_count: number;
    };
  };
  categories: {
    [key: string]: {
      ID: number;
      name: string;
      slug: string;
      description: string;
      post_count: number;
      parent: number;
    };
  };
}

export interface WordPressComResponse {
  found: number;
  posts: WordPressComPost[];
}

// WordPress.com API service class
class WordPressComApiService {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  // Fetch posts from WordPress.com API
  async getPosts(page: number = 1, perPage: number = POSTS_PER_PAGE): Promise<WordPressComPost[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/posts/`, {
        params: {
          number: perPage,
          page: page,
        },
      });
      return response.data.posts || [];
    } catch (error) {
      console.error('Error fetching WordPress.com posts:', error);
      return [];
    }
  }

  // Fetch a single post by slug
  async getPostBySlug(slug: string): Promise<WordPressComPost | null> {
    try {
      const response = await axios.get(`${this.baseUrl}/posts/slug:${slug}`);
      return response.data || null;
    } catch (error) {
      console.error('Error fetching WordPress.com post:', error);
      return null;
    }
  }

  // Fetch featured posts (first 3 posts as featured)
  async getFeaturedPosts(): Promise<WordPressComPost[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/posts/`, {
        params: {
          number: 3,
          page: 1,
        },
      });
      return response.data.posts || [];
    } catch (error) {
      console.error('Error fetching featured posts:', error);
      return [];
    }
  }

  // Search posts
  async searchPosts(query: string, page: number = 1): Promise<WordPressComPost[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/posts/`, {
        params: {
          search: query,
          number: POSTS_PER_PAGE,
          page: page,
        },
      });
      return response.data.posts || [];
    } catch (error) {
      console.error('Error searching WordPress.com posts:', error);
      return [];
    }
  }

  // Get posts by category
  async getPostsByCategory(categorySlug: string, page: number = 1): Promise<WordPressComPost[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/posts/`, {
        params: {
          category: categorySlug,
          number: POSTS_PER_PAGE,
          page: page,
        },
      });
      return response.data.posts || [];
    } catch (error) {
      console.error('Error fetching posts by category:', error);
      return [];
    }
  }

  // Get posts by tag
  async getPostsByTag(tagSlug: string, page: number = 1): Promise<WordPressComPost[]> {
    try {
      const response = await axios.get(`${this.baseUrl}/posts/`, {
        params: {
          tag: tagSlug,
          number: POSTS_PER_PAGE,
          page: page,
        },
      });
      return response.data.posts || [];
    } catch (error) {
      console.error('Error fetching posts by tag:', error);
      return [];
    }
  }
}

// Create and export the service instance
export const wordpressApi = new WordPressComApiService(WORDPRESS_COM_API_URL);

// Utility functions adapted for WordPress.com API format
export const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const getExcerpt = (post: WordPressComPost, maxLength: number = 150): string => {
  const excerpt = stripHtmlTags(post.excerpt);
  return excerpt.length > maxLength ? excerpt.substring(0, maxLength) + '...' : excerpt;
};

export const getFeaturedImage = (post: WordPressComPost): string | null => {
  if (post.featured_image) {
    return post.featured_image;
  }
  if (post.post_thumbnail?.URL) {
    return post.post_thumbnail.URL;
  }
  return null;
};

export const getAuthorName = (post: WordPressComPost): string => {
  return post.author?.name || 'Unknown Author';
};

export const getPostTags = (post: WordPressComPost): string[] => {
  return Object.values(post.tags || {}).map(tag => tag.name);
};

export const getPostCategories = (post: WordPressComPost): string[] => {
  return Object.values(post.categories || {}).map(category => category.name);
};

// Convert WordPress.com post to the format expected by the UI components
export const convertToWordPressPost = (wpComPost: WordPressComPost): any => {
  return {
    id: wpComPost.ID,
    date: wpComPost.date,
    slug: wpComPost.slug,
    title: {
      rendered: wpComPost.title
    },
    excerpt: {
      rendered: wpComPost.excerpt
    },
    content: {
      rendered: wpComPost.content
    },
    author: wpComPost.author?.ID || 0,
    featured_media: 0,
    categories: Object.keys(wpComPost.categories || {}),
    tags: Object.keys(wpComPost.tags || {}),
    _embedded: {
      author: [{
        name: wpComPost.author?.name || 'Unknown Author',
        slug: wpComPost.author?.nice_name || ''
      }],
      'wp:featuredmedia': wpComPost.featured_image || wpComPost.post_thumbnail?.URL ? [{
        source_url: wpComPost.featured_image || wpComPost.post_thumbnail?.URL || '',
        alt_text: wpComPost.title
      }] : undefined
    }
  };
}; 